/* Mélis-Mélos gravure-editor — MVP
   Session-only editor. Structured to drop into a Shopify theme app extension later:
   swap RING_SRC / DESIGN_AREA for per-product settings and wire EXPORT into the cart form. */

(function () {
  'use strict';

  // --- Per-product config (later injected from de Shopify product/variant) ---
  const CANVAS_SIZE = 600;

  // Alle bewerkbare instellingen (ringen, lettertypes, kleuren) staan in
  // assets/config.js zodat Marijn ze op één plek kan aanpassen. Hier lezen we
  // ze alleen uit; niets in dit bestand hoeft te wijzigen bij nieuwe ringen,
  // fonts of kleuren.
  const CONFIG = window.MM_CONFIG || { rings: {}, fonts: [], colors: [] };
  const RINGS = CONFIG.rings;
  const FONTS = CONFIG.fonts;
  const SWATCHES = CONFIG.colors;

  // Actief graveervlak; wordt bij elke kleurwissel opnieuw gezet.
  const FIRST_RING = Object.keys(RINGS)[0];
  // In Shopify hoort elke ringkleur bij één productpagina. Het sjabloon zet dan
  // window.MM_FORCE_RING op de juiste sleutel; de editor start met die ring en
  // verbergt de ring-kiezer. Lokaal is dit undefined en werkt alles zoals altijd.
  const FORCED_RING = (window.MM_FORCE_RING && RINGS[window.MM_FORCE_RING])
    ? window.MM_FORCE_RING : null;
  const START_RING = FORCED_RING || FIRST_RING;
  let DESIGN_AREA = RINGS[START_RING].design;

  // Symbolen — line-art motieven die de klant op de ring plaatst. Elk symbool is een
  // transparante PNG (donkere lijn, rest transparant); de graveerkleur wordt als tint
  // toegepast zodat het symbool meekleurt met de gekozen gravering. De lijst komt uit
  // assets/symbols/symbols.json, herbouwd door tools/build-symbols.py bij nieuwe uploads.
  const SYMBOLS = [];

  // Asset-URL resolver. Lokaal (index.html) blijven de paden uit config.js zoals ze
  // zijn ('assets/rings/x.png'). In Shopify staan alle assets plat in de thema-map
  // /assets/ met een CDN-URL per bestand; het thema-sjabloon vult dan window.MM_ASSET_URLS
  // met { 'x.png': '<asset_url>' }. We matchen op bestandsnaam. Data-URL's (foto-upload)
  // zitten niet in de map en gaan onveranderd door.
  function assetURL(p) {
    if (!p) return p;
    if (window.MM_ASSET_URLS) {
      const file = p.split('/').pop();
      if (window.MM_ASSET_URLS[file]) return window.MM_ASSET_URLS[file];
    }
    return p;
  }

  function loadSymbols() {
    // Shopify: het sjabloon zet de symbolenlijst direct in window.MM_SYMBOLS (geen fetch
    // van een submap nodig). Lokaal lezen we assets/symbols/symbols.json.
    if (Array.isArray(window.MM_SYMBOLS)) {
      window.MM_SYMBOLS.forEach((s) => SYMBOLS.push(s));
      return Promise.resolve();
    }
    return fetch('assets/symbols/symbols.json')
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => { list.forEach((s) => SYMBOLS.push(s)); })
      .catch(() => {});
  }

  // --- Editor state ---
  // Defaults volgen de config: eerste ring, eerste lettertype, en 'Black' als die
  // kleur bestaat (anders de eerste kleur uit de lijst).
  const DEFAULT_COLOR = SWATCHES.find((s) => s.name === 'Black') || SWATCHES[0];
  const state = {
    ring: START_RING,
    font: (FONTS[0] && FONTS[0].css) || 'serif',
    size: 40,
    color: DEFAULT_COLOR.hex,
    colorName: DEFAULT_COLOR.name
  };

  let canvas;

  // Clip every engraving object to the circular design area so nothing spills off the ring.
  function makeClip() {
    return new fabric.Ellipse({
      left: DESIGN_AREA.cx,
      top: DESIGN_AREA.cy,
      rx: DESIGN_AREA.rx,
      ry: DESIGN_AREA.ry,
      originX: 'center',
      originY: 'center',
      absolutePositioned: true
    });
  }

  // Representative radius for default placement/sizing (smallest half-axis of the oval).
  function areaR() {
    return Math.min(DESIGN_AREA.rx, DESIGN_AREA.ry);
  }

  // Krimp een net toegevoegde tekst zodat hij binnen het ovale graveervlak past.
  // De tekst wordt tóch al geklipt op de ovaal; zonder deze fit worden bij smallere
  // stenen (LIMA/NYGS) de eerste/laatste letters van een lange naam afgesneden.
  // We verkleinen alleen (nooit vergroten) en houden een klein randje (0.95) vrij.
  function fitTextToArea(text) {
    const rx = DESIGN_AREA.rx * 0.95;
    const ry = DESIGN_AREA.ry * 0.95;
    let guard = 0;
    while (text.fontSize > 12 && guard++ < 300) {
      const a = (text.width * text.scaleX) / 2;
      const b = (text.height * text.scaleY) / 2;
      if ((a * a) / (rx * rx) + (b * b) / (ry * ry) <= 1) break;
      text.set('fontSize', text.fontSize - 1);
      text.setCoords();
    }
  }

  // Only user-added engraving objects (exclude the ring background).
  function engravingObjects() {
    return canvas.getObjects().filter((o) => o.data && o.data.engraving);
  }

  function pinBackground() {
    canvas.getObjects().forEach((o) => {
      if (o.data && o.data.background) canvas.sendToBack(o);
    });
  }

  // Bouw automatisch de Google Fonts-<link> uit de lettertype-lijst in config.js,
  // zodat een nieuw font toevoegen alleen daar hoeft (index.html blijft ongemoeid).
  function injectFontStylesheet() {
    const families = FONTS.map((f) => f.google).filter(Boolean);
    if (!families.length) return;
    const href = 'https://fonts.googleapis.com/css2?family=' +
      families.join('&family=') + '&display=swap';
    if (document.querySelector('link[data-mm-fonts]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-mm-fonts', '');
    document.head.appendChild(link);
  }

  function loadFonts() {
    injectFontStylesheet();
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    return Promise.all(FONTS.map((f) => document.fonts.load('24px "' + f.css + '"'))).catch(() => {});
  }

  function buildFontSelect() {
    const sel = document.getElementById('fontSelect');
    FONTS.forEach((f) => {
      const opt = document.createElement('option');
      opt.value = f.css;
      opt.textContent = f.label;
      opt.style.fontFamily = "'" + f.css + "'";
      sel.appendChild(opt);
    });
    sel.value = state.font;
  }

  function loadRing(key) {
    return new Promise((resolve) => {
      fabric.Image.fromURL(assetURL(RINGS[key].src), (img) => {
        canvas.getObjects()
          .filter((o) => o.data && o.data.background)
          .forEach((o) => canvas.remove(o));
        const scale = CANVAS_SIZE / (img.width || CANVAS_SIZE);
        img.set({
          left: 0,
          top: 0,
          scaleX: scale,
          scaleY: scale,
          selectable: false,
          evented: false,
          hoverCursor: 'default'
        });
        img.data = { background: true };
        canvas.add(img);
        canvas.sendToBack(img);
        canvas.requestRenderAll();
        resolve();
      }, { crossOrigin: 'anonymous' });
    });
  }

  // Swap the ring color: load the matching photo and re-clip engravings to its area.
  // In Shopify this is called with the color of the selected product variant.
  async function setRing(key) {
    if (!RINGS[key]) return;
    state.ring = key;
    DESIGN_AREA = RINGS[key].design;
    await loadRing(key);
    engravingObjects().forEach((o) => { o.clipPath = makeClip(); });
    canvas.requestRenderAll();
    markActiveRing(key);
  }

  function buildRingSelect() {
    const wrap = document.getElementById('ringColors');
    // Vaste ring (Shopify-productpagina): geen kiezer tonen.
    if (FORCED_RING) {
      const grp = wrap && wrap.closest('.group');
      if (grp) grp.style.display = 'none';
      return;
    }
    Object.keys(RINGS).forEach((key) => {
      const r = RINGS[key];
      const b = document.createElement('button');
      b.className = 'ring-chip';
      b.dataset.ring = key;
      b.title = r.label;
      b.innerHTML =
        '<span class="dot" style="background:' + r.hex + '"></span>' +
        '<span class="lbl">' + r.label + '</span>';
      b.addEventListener('click', () => setRing(key));
      wrap.appendChild(b);
    });
  }

  function markActiveRing(key) {
    document.querySelectorAll('.ring-chip').forEach((el) => {
      el.classList.toggle('active', el.dataset.ring === key);
    });
  }

  function addText() {
    const input = document.getElementById('textInput');
    const value = (input.value || '').trim();
    if (!value) {
      input.focus();
      return;
    }
    const text = new fabric.IText(value, {
      left: DESIGN_AREA.cx,
      top: DESIGN_AREA.cy,
      originX: 'center',
      originY: 'center',
      fontFamily: state.font,
      fontSize: state.size,
      fill: state.color,
      textAlign: 'center',
      editable: true,
      clipPath: makeClip()
    });
    text.data = { engraving: true, type: 'text' };
    fitTextToArea(text);
    canvas.add(text);
    canvas.setActiveObject(text);
    pinBackground();
    canvas.requestRenderAll();
    input.value = '';
    syncControls();
  }

  // Recolor a symbol image to the engraving color. The symbol pixels are near-black with
  // per-pixel alpha, so a full-strength 'tint' repaints the line-art while keeping its shape.
  function tintSymbol(img, hex) {
    img.filters = [new fabric.Image.filters.BlendColor({ color: hex, mode: 'tint', alpha: 1 })];
    img.applyFilters();
  }

  // Rendered on-canvas size (largest side, in px) of a symbol; the setter keeps the aspect ratio.
  function symbolSize(o) {
    return Math.round(Math.max(o.width || 1, o.height || 1) * o.scaleX);
  }
  function setSymbolSize(o, px) {
    const scale = px / Math.max(o.width || 1, o.height || 1);
    o.set({ scaleX: scale, scaleY: scale });
  }

  function addSymbol(src, source) {
    fabric.Image.fromURL(assetURL(src), (img) => {
      const target = areaR() * 1.4;
      const scale = target / Math.max(img.width || target, img.height || target);
      img.set({
        left: DESIGN_AREA.cx,
        top: DESIGN_AREA.cy,
        originX: 'center',
        originY: 'center',
        scaleX: scale,
        scaleY: scale,
        clipPath: makeClip()
      });
      img.data = { engraving: true, type: 'symbol', source: source || src };
      tintSymbol(img, state.color);
      canvas.add(img);
      canvas.setActiveObject(img);
      pinBackground();
      canvas.requestRenderAll();
      syncControls();
    }, { crossOrigin: 'anonymous' });
  }

  // Turn an uploaded photo into engraving-ready line-art: downscale, blur, Sobel edge
  // detection, threshold. Output is a transparent PNG with dark ink (25,25,25) and
  // per-pixel alpha along the edges — the exact shape tintSymbol() expects, so the
  // result behaves like any other symbol (recolor, scale, rotate, drag).
  function photoToLineArt(imgEl) {
    const MAX = 500;
    const iw = imgEl.naturalWidth || imgEl.width;
    const ih = imgEl.naturalHeight || imgEl.height;
    const scale = Math.min(1, MAX / Math.max(iw, ih));
    const w = Math.max(1, Math.round(iw * scale));
    const h = Math.max(1, Math.round(ih * scale));

    const src = document.createElement('canvas');
    src.width = w; src.height = h;
    const sctx = src.getContext('2d');
    sctx.drawImage(imgEl, 0, 0, w, h);
    const px = sctx.getImageData(0, 0, w, h).data;

    // Luminance, then a light 3x3 box blur to suppress sensor noise before edge detection.
    const gray = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) {
      gray[i] = 0.299 * px[i * 4] + 0.587 * px[i * 4 + 1] + 0.114 * px[i * 4 + 2];
    }
    const blur = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let s = 0, n = 0;
        for (let j = -1; j <= 1; j++) {
          for (let i = -1; i <= 1; i++) {
            const yy = y + j, xx = x + i;
            if (yy < 0 || yy >= h || xx < 0 || xx >= w) continue;
            s += gray[yy * w + xx]; n++;
          }
        }
        blur[y * w + x] = s / n;
      }
    }

    const gxk = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    const gyk = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
    const out = sctx.createImageData(w, h);
    const o = out.data;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        let sx = 0, sy = 0, k = 0;
        for (let j = -1; j <= 1; j++) {
          for (let i = -1; i <= 1; i++) {
            const v = blur[(y + j) * w + (x + i)];
            sx += v * gxk[k]; sy += v * gyk[k]; k++;
          }
        }
        const mag = Math.sqrt(sx * sx + sy * sy);
        // Soft threshold: ignore faint gradients, ramp the rest up to full opacity.
        const a = mag < 55 ? 0 : Math.min(255, (mag - 55) * 1.7);
        const idx = (y * w + x) * 4;
        o[idx] = 25; o[idx + 1] = 25; o[idx + 2] = 25; o[idx + 3] = a;
      }
    }
    sctx.putImageData(out, 0, 0);
    return src.toDataURL('image/png');
  }

  function handlePhotoFile(file) {
    if (!file || !/^image\//.test(file.type)) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => addSymbol(photoToLineArt(img), 'foto-upload');
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  }

  function applyColorToAll(hex, name) {
    state.color = hex;
    if (name) state.colorName = name;
    engravingObjects().forEach((o) => {
      if (o.data.type === 'symbol') tintSymbol(o, hex);
      else o.set('fill', hex);
    });
    canvas.requestRenderAll();
    markActiveSwatch(hex);
  }

  // Capture the finished design for the order. Produces three things the workshop/cart need:
  //   previewImage   — full ring + engraving, for the customer's cart line and confirmation
  //   engravingImage — engraving only (transparent, cropped to the face, hi-res) for engraving
  //   spec           — machine-readable description (ring, color, every element)
  // In Shopify this is called by the "In winkelwagen" handler; the images become uploaded
  // line-item files and the spec fields become line-item properties.
  function exportDesign() {
    canvas.discardActiveObject();
    canvas.requestRenderAll();

    const previewImage = canvas.toDataURL({ format: 'png', multiplier: 1 });

    // Het schone ontwerpbestand voor het atelier: alle gravering tijdelijk egaal
    // zwart maken, ring/achtergrond verbergen en strak op het graveervlak snijden.
    // Daarna de gekozen kleur weer terugzetten (previewImage houdt de echte kleur).
    const keepColor = state.color;
    const keepName = state.colorName;
    applyColorToAll('#000000');

    const bg = canvas.getObjects().find((o) => o.data && o.data.background);
    const prevBg = canvas.backgroundColor;
    if (bg) bg.visible = false;
    canvas.backgroundColor = null;
    canvas.requestRenderAll();
    const d = DESIGN_AREA;
    const engravingImage = canvas.toDataURL({
      format: 'png',
      left: d.cx - d.rx, top: d.cy - d.ry,
      width: d.rx * 2, height: d.ry * 2,
      multiplier: 3
    });
    if (bg) bg.visible = true;
    canvas.backgroundColor = prevBg;
    applyColorToAll(keepColor, keepName);
    canvas.requestRenderAll();

    const elements = engravingObjects().map((o) => {
      const el = {
        type: o.data.type,
        left: Math.round(o.left),
        top: Math.round(o.top),
        angle: Math.round(o.angle || 0)
      };
      if (o.data.type === 'text') {
        el.value = o.text;
        el.font = o.fontFamily;
        el.fontSize = Math.round(o.fontSize);
      } else {
        el.size = symbolSize(o);
        el.source = (o.data && o.data.source) || null;
      }
      return el;
    });

    return {
      ring: state.ring,
      ringLabel: RINGS[state.ring].label,
      engravingColor: state.colorName,
      engravingHex: state.color,
      elementCount: elements.length,
      elements: elements,
      previewImage: previewImage,
      engravingImage: engravingImage
    };
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = src;
    });
  }

  // Bouwt één "werkblad"-afbeelding die met de bestelling meegaat: links hoe de
  // gravering op de ring komt te staan (verhouding/plaatsing zichtbaar), rechts
  // het schone ontwerp egaal zwart. Boven staan de ring- en kleurnaam.
  function buildWorksheet(data) {
    return Promise.all([
      loadImage(data.previewImage),
      loadImage(data.engravingImage)
    ]).then(([preview, design]) => {
      const W = 1200, H = 760, pad = 40;
      const cv = document.createElement('canvas');
      cv.width = W; cv.height = H;
      const ctx = cv.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);
      ctx.textBaseline = 'top';

      ctx.fillStyle = '#111111';
      ctx.font = '600 30px Georgia, "Times New Roman", serif';
      ctx.fillText('Mélis-Mélos — graveerontwerp', pad, pad);

      ctx.font = '400 22px Georgia, "Times New Roman", serif';
      ctx.fillStyle = '#333333';
      ctx.fillText('Ring: ' + data.ringLabel, pad, pad + 44);
      ctx.fillText('Graveerkleur: ' + data.engravingColor, pad, pad + 74);

      const panelTop = pad + 120;
      const colW = (W - pad * 3) / 2;
      const size = Math.min(colW, H - panelTop - pad - 30);

      // Links: op de ring (verhouding/plaatsing)
      const leftX = pad;
      ctx.drawImage(preview, leftX, panelTop, size, size);
      ctx.font = '400 18px Georgia, "Times New Roman", serif';
      ctx.fillStyle = '#555555';
      ctx.fillText('Zo staat het op de ring', leftX, panelTop + size + 8);

      // Rechts: schoon ontwerp, egaal zwart
      const rightX = pad * 2 + colW;
      ctx.fillStyle = '#f4f1ea';
      ctx.fillRect(rightX, panelTop, colW, size);
      ctx.strokeStyle = '#d8d2c4';
      ctx.strokeRect(rightX, panelTop, colW, size);
      const m = 44;
      const boxW = colW - m * 2, boxH = size - m * 2;
      const dr = Math.min(boxW / design.width, boxH / design.height);
      const dw = design.width * dr, dh = design.height * dr;
      ctx.drawImage(design, rightX + (colW - dw) / 2, panelTop + (size - dh) / 2, dw, dh);
      ctx.fillStyle = '#555555';
      ctx.fillText('Ontwerp (egaal zwart)', rightX, panelTop + size + 8);

      return cv.toDataURL('image/png');
    });
  }

  function withSelected(fn) {
    const obj = canvas.getActiveObject();
    if (!obj || !(obj.data && obj.data.engraving)) return;
    fn(obj);
    canvas.requestRenderAll();
  }

  // --- UI wiring ---
  function buildSwatches() {
    const wrap = document.getElementById('swatches');
    SWATCHES.forEach((s) => {
      const b = document.createElement('button');
      b.className = 'swatch';
      b.style.background = s.hex;
      b.title = s.name;
      b.setAttribute('aria-label', s.name);
      b.addEventListener('click', () => applyColorToAll(s.hex, s.name));
      b.dataset.hex = s.hex.toLowerCase();
      b.dataset.name = s.name;
      wrap.appendChild(b);
    });
  }

  function buildSymbolLibrary() {
    const wrap = document.getElementById('symbols');
    SYMBOLS.forEach((s) => {
      const b = document.createElement('button');
      b.className = 'symbol-chip';
      b.title = s.name;
      b.setAttribute('aria-label', s.name);
      const im = document.createElement('img');
      im.src = assetURL(s.src);
      im.alt = s.name;
      b.appendChild(im);
      b.addEventListener('click', () => addSymbol(s.src));
      wrap.appendChild(b);
    });
  }

  function markActiveSwatch(hex) {
    const h = (hex || '').toLowerCase();
    document.querySelectorAll('.swatch').forEach((el) => {
      el.classList.toggle('active', el.dataset.hex === h);
    });
  }

  function syncControls() {
    const obj = canvas.getActiveObject();
    const hint = document.getElementById('selHint');
    const isText = obj && obj.data && obj.data.type === 'text';
    if (obj && obj.data && obj.data.engraving) {
      hint.textContent = isText
        ? 'Tekst geselecteerd — pas lettertype, grootte of kleur aan.'
        : 'Symbool geselecteerd — sleep, schaal met de hoeken of de schuif, en draai het.';
      if (isText) {
        state.font = obj.fontFamily;
        document.getElementById('fontSelect').value = obj.fontFamily;
        state.size = Math.round(obj.fontSize);
        document.getElementById('sizeRange').value = state.size;
        document.getElementById('sizeVal').textContent = state.size;
      } else {
        const px = symbolSize(obj);
        document.getElementById('sizeRange').value = px;
        document.getElementById('sizeVal').textContent = px;
      }
    } else {
      hint.textContent = 'Niets geselecteerd. Klik op een element om het te bewerken.';
    }
  }

  function bindUI() {
    document.getElementById('addText').addEventListener('click', addText);
    document.getElementById('textInput').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') addText();
    });

    document.getElementById('fontSelect').addEventListener('change', (e) => {
      state.font = e.target.value;
      withSelected((o) => {
        if (o.data.type === 'text') o.set('fontFamily', state.font);
      });
    });

    const sizeRange = document.getElementById('sizeRange');
    sizeRange.addEventListener('input', (e) => {
      const v = parseInt(e.target.value, 10);
      document.getElementById('sizeVal').textContent = v;
      const obj = canvas.getActiveObject();
      if (obj && obj.data && obj.data.type === 'symbol') {
        setSymbolSize(obj, v);
        canvas.requestRenderAll();
      } else {
        state.size = v;
        withSelected((o) => {
          if (o.data.type === 'text') o.set('fontSize', v);
        });
      }
    });

    const photoInput = document.getElementById('photoInput');
    if (photoInput) {
      photoInput.addEventListener('change', (e) => {
        handlePhotoFile(e.target.files && e.target.files[0]);
        e.target.value = '';
      });
    }

    document.getElementById('bringFront').addEventListener('click', () => {
      withSelected((o) => canvas.bringToFront(o));
    });
    document.getElementById('sendBack').addEventListener('click', () => {
      withSelected((o) => {
        canvas.sendToBack(o);
        pinBackground();
      });
    });
    document.getElementById('deleteSel').addEventListener('click', () => {
      const obj = canvas.getActiveObject();
      if (obj && obj.data && obj.data.engraving) {
        canvas.remove(obj);
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        syncControls();
      }
    });
    document.getElementById('confirmDesign').addEventListener('click', async () => {
      const hint = document.getElementById('confirmHint');
      if (engravingObjects().length === 0) {
        hint.textContent = 'Voeg eerst tekst, een symbool of een foto toe.';
        return;
      }
      const data = exportDesign();
      // Combineer beide beelden tot één werkblad-afbeelding die met de order meegaat.
      try {
        data.worksheetImage = await buildWorksheet(data);
      } catch (e) {
        data.worksheetImage = data.engravingImage;
      }
      window.MM_LAST_EXPORT = data;
      // Integratie-haak: het Shopify-sjabloon luistert hierop en zet het
      // graveerbestand in het Globo-uploadveld + vult de kleur. Lokaal luistert
      // er niets, dus dit is onschadelijk buiten Shopify.
      document.dispatchEvent(new CustomEvent('mm:confirmed', { detail: data }));
      // Buiten Shopify: download het werkblad zodat je het resultaat ziet.
      if (!window.MM_SHOPIFY) {
        const a = document.createElement('a');
        a.href = data.worksheetImage;
        a.download = 'melismelos-gravure.png';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      hint.textContent = window.MM_SHOPIFY
        ? 'Ontwerp vastgelegd — je gravering is toegevoegd. Klik nu op “In winkelwagen”.'
        : 'Ontwerp vastgelegd — voorbeeld gedownload. Klaar om te bestellen.';
    });

    document.getElementById('clearAll').addEventListener('click', () => {
      engravingObjects().forEach((o) => canvas.remove(o));
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      syncControls();
    });

    document.addEventListener('keydown', (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace')) {
        const obj = canvas.getActiveObject();
        if (obj && obj.isEditing) return; // don't hijack text editing
        if (obj && obj.data && obj.data.engraving) {
          e.preventDefault();
          canvas.remove(obj);
          canvas.discardActiveObject();
          canvas.requestRenderAll();
          syncControls();
        }
      }
    });

    canvas.on('selection:created', syncControls);
    canvas.on('selection:updated', syncControls);
    canvas.on('selection:cleared', syncControls);

    // Keep the size slider in sync while dragging a symbol's corner handles.
    function reflectSymbolSize() {
      const obj = canvas.getActiveObject();
      if (obj && obj.data && obj.data.type === 'symbol') {
        const px = symbolSize(obj);
        document.getElementById('sizeRange').value = px;
        document.getElementById('sizeVal').textContent = px;
      }
    }
    canvas.on('object:scaling', reflectSymbolSize);
    canvas.on('object:modified', reflectSymbolSize);
  }

  async function init() {
    canvas = new fabric.Canvas('c', {
      selection: false,
      preserveObjectStacking: true,
      backgroundColor: '#ffffff'
    });
    buildRingSelect();
    buildFontSelect();
    buildSwatches();
    markActiveSwatch(state.color);
    bindUI();
    await loadFonts();
    await loadSymbols();
    buildSymbolLibrary();
    await setRing(state.ring);
    window.MM_CANVAS = canvas; // debug hook (verwijderen bij Shopify-integratie)
    window.MM_EXPORT = exportDesign; // aanroepen vanuit de Shopify "In winkelwagen"-handler
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
